import { useTranslation } from "react-i18next";
import { IWebsiteBuilderSettingsSheetProps, WebsiteBuilderSettingsSheet } from "../designSpace/views/hooks/helpers/WebsiteBuilderSettingsSheet";
import { FormBuilderEditor } from "./editor/FormBuilderEditor";
import React, { useRef, useState } from "react";
import { FormBuilderLibrary } from "./library/FormBuilderLibrary";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { websiteBuilderAddForm, websiteBuilderUpdateForm } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { IFormBuilderForm } from "./editor/model/formBuilderForm";

interface IFormBuilderProps {
  selectedFormId: number;
  setSelectedFormId: (formId: number) => void;
  closeFormBuilder: () => void;
}

type TFormBuilderView = "library" | "editor";

export type TFormBuilderSaveFormRef = React.MutableRefObject<IFormBuilderForm>;

export function FormBuilder(props: IFormBuilderProps) {
  const { selectedFormId, setSelectedFormId, closeFormBuilder } = props;

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const saveFormRef: TFormBuilderSaveFormRef = useRef(null);

  const [view, setView] = useState<TFormBuilderView>(selectedFormId == null ? "library" : "editor");

  function saveForm() {
    if (saveFormRef.current == null) {
      dispatch(showError(t("Form not found")));

      return;
    }
    
    if (saveFormRef.current.id == null) {
      void dispatch(websiteBuilderAddForm(saveFormRef.current, t, (id: number) => {
        setSelectedFormId(id);
        closeFormBuilder();
      }));
    } else {
      void dispatch(websiteBuilderUpdateForm(saveFormRef.current, t, () => {
        closeFormBuilder();
      }));
    }
  }

  function getSettingsSheetProps(): IWebsiteBuilderSettingsSheetProps {
    const sharedProps: Pick<IWebsiteBuilderSettingsSheetProps, "size" | "onCancel"> = {
      size: "full",
      onCancel: closeFormBuilder,
    };

    if (view === "editor") {
      return {
        title: t("Form builder"),
        supportText: t("Manage your form to track submissions from your website."),
        okText: t("Save changes"),
        secondaryText: t("View all forms"),
        onSecondary: () => setView("library"),
        onOk: saveForm,
        children: <FormBuilderEditor selectedFormId={selectedFormId} saveFormRef={saveFormRef} />,
        ...sharedProps,
      };
    } else {
      return {
        title: t("My forms"),
        supportText: t("Select a form for this container, or create a new one."),
        okText: t("Continue"),
        onOk: () => setView("editor"),
        children: <FormBuilderLibrary selectedFormId={selectedFormId} setSelectedFormId={setSelectedFormId} />,
        ...sharedProps,
      };
    }
  }

  const settingsSheetProps = getSettingsSheetProps();

  return (
    <WebsiteBuilderSettingsSheet {...settingsSheetProps} />
  );
}